"use client";

import { PopoverCloseIcon, SiderOpenIcon } from "@/assets/icons";
import { ThreadMessageProvider } from "@/context/ThreadMessageContext";
import { useActiveSection } from "@/hooks/useActiveSection";
import { useExportStatus } from "@/hooks/useExportStatus";
import { useLatestArtifacts } from "@/hooks/useLatestArtifacts";
import { useLatestRtc } from "@/hooks/useLatestRtc";
import { useLatestThread } from "@/hooks/useLatestThread";
import { useLatestThreadOptions } from "@/hooks/useLatestThreadOptions";
import {
  loadChatWidth,
  persistChatWidth,
} from "@/store/helpers/uiPreferenceStorage";
import { threadStore } from "@/store/thread";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "../../component/Button";
import { useArtifactsViewerData } from "../../hooks/useArtifactsViewerData";
import { useBreakpoints } from "../../hooks/useBreakpoints";
import { useThreadExportProgress } from "../../hooks/useThreadExportProgress";
import type { ArtifactsViewerRef } from "../ArtifactsViewer";
import { ArtifactsViewer } from "../ArtifactsViewer";
import { VisualTrackKind } from "../ArtifactsViewer/components/TimelineEditor";
import { ExportProgress } from "../ArtifactsViewer/components/TimelineEditor/components/ExportProgress";
import { ArtifactsViewerViewMode } from "../ArtifactsViewer/type";
import { ChatUI } from "../ChatUI";
import { ArtifactHeader } from "./components/ArtifactHeader";
import { ArtifactNavigator } from "./components/ArtifactNavigator";
import { ThreadNotFound } from "./components/ThreadNotFound";
import { TimelineToastProvider } from "./components/TimelineToast";
import { useThreadSession } from "./useThreadSession";

interface IProps {
  threadId: string;
}

const DEFAULT_VIEW_MODE = ArtifactsViewerViewMode.STORYBOARD;
const DEFAULT_TIMELINE_TYPE: VisualTrackKind = "shot";
const MIN_CHAT_WIDTH = 484;
const MAX_CHAT_WIDTH = 750;
const DEFAULT_CHAT_WIDTH = MIN_CHAT_WIDTH;

const clampChatWidth = (value: number) =>
  Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, value));

const getViewStateFromLocation = () => {
  if (typeof window === "undefined") {
    return {
      viewMode: DEFAULT_VIEW_MODE,
      timelineType: DEFAULT_TIMELINE_TYPE,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get("view");
  const trackParam = params.get("track");

  const viewMode =
    viewParam === ArtifactsViewerViewMode.TIMELINE
      ? ArtifactsViewerViewMode.TIMELINE
      : DEFAULT_VIEW_MODE;

  const timelineType: VisualTrackKind =
    trackParam === "video" || trackParam === "shot"
      ? trackParam
      : DEFAULT_TIMELINE_TYPE;

  return { viewMode, timelineType };
};

export const ThreadDetail = observer((props: IProps) => {
  const { threadId } = props;
  useLatestThread(threadId);
  useLatestArtifacts(threadId);
  useLatestRtc(threadId);
  useLatestThreadOptions(threadId);
  useThreadSession(threadId);
  const artifactsViewerRef = useRef<ArtifactsViewerRef>(null);
  const [artifactsScrollNode, setArtifactsScrollNode] =
    useState<HTMLElement | null>(null);
  const initialViewState = useMemo(getViewStateFromLocation, []);
  const [viewMode, setViewMode] = useState<ArtifactsViewerViewMode>(
    initialViewState.viewMode
  );
  const [timelineType, setTimelineType] = useState<VisualTrackKind>(
    initialViewState.timelineType
  );
  const artifacts = useArtifactsViewerData();
  const [chatWidth, setChatWidth] = useState<number>(DEFAULT_CHAT_WIDTH);

  const { isSmallScreen } = useBreakpoints();
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const storedWidth = await loadChatWidth();
      if (!active) return;
      if (typeof storedWidth === "number" && Number.isFinite(storedWidth)) {
        setChatWidth(clampChatWidth(storedWidth));
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isSmallScreen) {
      setIsChatOpen(true);
    }
  }, [isSmallScreen]);

  // 使用自定义 Hook 计算所有导出相关状态
  const exportStatus = useExportStatus();
  const { actions: exportProgressActions } = useThreadExportProgress(threadId);
  const isRendering = !!exportStatus.renderTask;
  const hasResultUrl = !!exportStatus.resultUrl;

  useEffect(() => {
    if (exportStatus.renderTask) {
      void exportProgressActions.markExportProgress(exportStatus.renderTask);
    }
  }, [exportStatus.renderTask, exportProgressActions]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const body = document.body;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyHeight = body.style.height;
    const prevHtmlHeight = html.style.height;

    html.classList.add("h-full", "overscroll-none");
    body.classList.add("h-full", "overscroll-none");
    if (!prevBodyOverscroll) body.style.overscrollBehavior = "none";
    if (!prevHtmlOverscroll) html.style.overscrollBehavior = "none";
    if (!prevBodyHeight) body.style.height = "100%";
    if (!prevHtmlHeight) html.style.height = "100%";

    return () => {
      body.classList.remove("overscroll-none", "h-full");
      html.classList.remove("overscroll-none", "h-full");
      if (prevBodyOverscroll) {
        body.style.overscrollBehavior = prevBodyOverscroll;
      } else {
        body.style.removeProperty("overscroll-behavior");
      }
      if (prevHtmlOverscroll) {
        html.style.overscrollBehavior = prevHtmlOverscroll;
      } else {
        html.style.removeProperty("overscroll-behavior");
      }
      if (prevBodyHeight) {
        body.style.height = prevBodyHeight;
      } else {
        body.style.removeProperty("height");
      }
      if (prevHtmlHeight) {
        html.style.height = prevHtmlHeight;
      } else {
        html.style.removeProperty("height");
      }
    };
  }, []);

  const isThreadNotFound = threadStore.artifactsRequestStatus === "not_found";
  const syncViewModeToUrl = useCallback(
    (mode: ArtifactsViewerViewMode, track: VisualTrackKind) => {
      if (typeof window === "undefined") return;
      const params = new URLSearchParams(window.location.search);
      params.set("view", mode);
      if (mode === ArtifactsViewerViewMode.TIMELINE) {
        params.set("track", track);
      } else {
        params.delete("track");
      }
      const query = params.toString();
      const nextUrl = query
        ? `${window.location.pathname}?${query}`
        : window.location.pathname;
      window.history.replaceState(window.history.state, "", nextUrl);
    },
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = () => {
      const { viewMode: urlViewMode, timelineType: urlTimelineType } =
        getViewStateFromLocation();
      setViewMode(urlViewMode);
      setTimelineType(urlTimelineType);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleScrollableNode = useCallback((node: HTMLElement | null) => {
    setArtifactsScrollNode(node);
  }, []);

  const activeSectionId = useActiveSection(artifactsScrollNode);
  const handleNavigateToSection = useCallback(
    (sectionId: string) => {
      if (viewMode !== ArtifactsViewerViewMode.STORYBOARD) {
        setViewMode(ArtifactsViewerViewMode.STORYBOARD);
        syncViewModeToUrl(
          ArtifactsViewerViewMode.STORYBOARD,
          DEFAULT_TIMELINE_TYPE
        );
      }
      artifactsViewerRef.current?.scrollToSection(sectionId);
    },
    [syncViewModeToUrl, viewMode]
  );

  const handleTimelineTypeChange = useCallback(
    (type: VisualTrackKind) => {
      setTimelineType(type);
      if (viewMode === ArtifactsViewerViewMode.TIMELINE) {
        syncViewModeToUrl(ArtifactsViewerViewMode.TIMELINE, type);
      }
    },
    [syncViewModeToUrl, viewMode]
  );

  const handleViewModeChange = useCallback(
    (mode: ArtifactsViewerViewMode) => {
      if (mode === viewMode) return;

      if (mode === ArtifactsViewerViewMode.TIMELINE) {
        setTimelineType(timelineType);
      }

      setViewMode(mode);
      syncViewModeToUrl(mode, timelineType);
    },
    [syncViewModeToUrl, timelineType, viewMode, artifacts]
  );

  const handleDragStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = chatWidth;

      const handleDrag = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        // 向左拖动增大聊天区域，向右拖动减小
        const nextWidth = clampChatWidth(startWidth - delta);
        setChatWidth((prev) => {
          if (prev === nextWidth) return prev;
          void persistChatWidth(nextWidth);
          return nextWidth;
        });
      };

      const handleDragEnd = () => {
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("mouseup", handleDragEnd);
      };

      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("mouseup", handleDragEnd);
    },
    [chatWidth]
  );

  if (isThreadNotFound) {
    return <ThreadNotFound />;
  }

  const chatPanelStyle = isSmallScreen
    ? undefined
    : { width: `${chatWidth}px`, minWidth: `${MIN_CHAT_WIDTH}px` };

  return (
    <ThreadMessageProvider threadId={threadId}>
      <div className="relative flex h-screen bg-background-container overflow-hidden">
        {/* Artifacts Viewer */}
        <div className="relative flex-1 h-full">
          <TimelineToastProvider>
            {/* 导出进度浮动提示 - 仅在 timeline 模式、thread 所有者且有导出状态时显示 */}
            {!!threadStore.thread?.owner &&
              viewMode === ArtifactsViewerViewMode.TIMELINE &&
              (isRendering || hasResultUrl) && (
                <ExportProgress
                  exportStatus={exportStatus}
                  threadId={threadId}
                  className="top-20"
                />
              )}

            <ArtifactHeader
              threadId={threadId}
              viewMode={viewMode}
              timelineType={timelineType}
              onTimelineTypeChange={handleTimelineTypeChange}
              exportStatus={exportStatus}
            />

            <div className="absolute flex inset-0 pt-[76px]">
              <ArtifactNavigator
                threadId={threadId}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                onNavigateToSection={handleNavigateToSection}
                activeSectionId={activeSectionId}
                scrollContainer={artifactsScrollNode}
              />
              <ArtifactsViewer
                ref={artifactsViewerRef}
                artifacts={artifacts}
                threadId={threadId}
                viewMode={viewMode}
                timelineType={timelineType}
                onScrollableNode={handleScrollableNode}
                onViewModeChange={handleViewModeChange}
                onTimelineTypeChange={handleTimelineTypeChange}
              />
            </div>
          </TimelineToastProvider>
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          className="hidden w-6 cursor-col-resize lg:block"
          onMouseDown={handleDragStart}
        ></div>

        {/* Chat UI */}
        <div
          className="absolute inset-0 z-[90] bg-black/80"
          style={{ display: isChatOpen && isSmallScreen ? "block" : "none" }}
          onClick={() => setIsChatOpen(false)}
        />

        <div
          className={`${
            isSmallScreen
              ? `absolute left-0 top-0 z-[100] h-full overflow-visible transition-[width] duration-300 ${
                  isChatOpen ? "w-full max-w-[520px]" : "w-0"
                }`
              : "h-full p-4 pl-0"
          }`}
          style={chatPanelStyle}
        >
          {isSmallScreen && isChatOpen && (
            <Button
              type="plain"
              className="fixed w-8 h-8 top-4 right-4 z-[100] text-text-primary bg-background-elevated-2 rounded-lg"
              icon={<PopoverCloseIcon className="w-6 h-6" />}
              onClick={() => setIsChatOpen(false)}
            ></Button>
          )}

          {isSmallScreen && (
            <button
              type="button"
              aria-label="Expand chat"
              className="fixed left-3 w-15 top-1/2 flex flex-col translate-y-[120px] items-center justify-center gap-1 text-text-primary"
              onClick={() => setIsChatOpen(true)}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-elevated-2">
                <SiderOpenIcon className="w-4 h-4" />
              </span>
              <span className="text-xs">Chat</span>
            </button>
          )}

          <div
            className={`h-full w-full ${
              isSmallScreen
                ? `transition-transform duration-300 ${
                    isChatOpen ? "translate-x-0" : "translate-x-[-100%]"
                  }`
                : ""
            }`}
          >
            <ChatUI />
          </div>
        </div>
      </div>
    </ThreadMessageProvider>
  );
});
